import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { DATA_REFRESH_RATE_IN_MILLIS_LONG } from 'src/utils/constants/constants';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { CatalogClient } from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { useCatalogDataContext } from '../CatalogDataContext';
import { useCatalogMetricsContext } from './useCatalogMetricsContext';

const filterEventByVenueAndPerformer = (
  eventVenueId: number | null,
  eventPerformerId: number | null,
  venueIds: number[] | null | undefined,
  performerIds: number[] | null | undefined
) => {
  const sanitizedVenueIds = venueIds ?? [];
  const sanitizedPerformerIds = performerIds ?? [];

  // No venue or performer filter applied, include all events
  if (sanitizedVenueIds.length === 0 && sanitizedPerformerIds.length === 0) {
    return true;
  }

  const venueMatch =
    eventVenueId != null && sanitizedVenueIds.includes(eventVenueId);
  const performerMatch =
    eventPerformerId != null &&
    sanitizedPerformerIds.includes(eventPerformerId);

  // Include event if it matches either venue or performer filter
  // Because we can only have either venue or performer filter at a time in the UI
  return venueMatch || performerMatch;
};

export function useGeSummaryMetricsQuery<
  TMetrics extends { currency: string | null },
>(
  queryKey: string,
  performerIds: number[] | null | undefined,
  venueIds: number[] | null | undefined,
  mergeCatalogMetrics: (metrics: TMetrics[]) => TMetrics[],
  getCatalogSummaryMetrics?: (
    client: CatalogClient,
    allMetrics: TMetrics[],
    mergeCatalogMetrics: (metrics: TMetrics[]) => TMetrics[]
  ) => Promise<TMetrics>,
  disabled?: boolean
) {
  const { trackError } = useClientLogContext();
  const { eventsTransformed } = useCatalogDataContext();
  const { activeAccountWebClientConfig } = useAppContext();

  const eventIdToIdsMap = useMemo(
    () =>
      (eventsTransformed ?? []).reduce((acc, ev) => {
        acc.set(ev.event.virtId, {
          venueId: ev.event.venId,
          performerId: ev.event.perfId,
        });
        return acc;
      }, new Map<string, { venueId: number | null; performerId: number | null }>()),
    [eventsTransformed]
  );

  const { eventDetailedMetrics, eventMetrics } =
    useCatalogMetricsContext<TMetrics>();

  const metricsToSummarize = useMemo(() => {
    const detailedMetrics = Object.entries(eventDetailedMetrics ?? {});
    const normalMetrics = Object.entries(eventMetrics ?? {});
    const metrics = (detailedMetrics.length ? detailedMetrics : normalMetrics)
      // Ensure that the event is in the current filter
      .filter(([evId]) => eventIdToIdsMap.has(evId))
      .filter(([evId]) => {
        const ids = eventIdToIdsMap.get(evId);
        return filterEventByVenueAndPerformer(
          ids?.venueId ?? null,
          ids?.performerId ?? null,
          venueIds,
          performerIds
        );
      })
      .map(([_, evMetrics]) => evMetrics);

    return mergeCatalogMetrics(metrics.filter((m) => m != null).map((m) => m!));
  }, [
    eventDetailedMetrics,
    eventIdToIdsMap,
    eventMetrics,
    mergeCatalogMetrics,
    performerIds,
    venueIds,
  ]);

  const shouldQuery =
    !(
      !getCatalogSummaryMetrics || !activeAccountWebClientConfig.activeAccountId
    ) &&
    metricsToSummarize.length > 0 &&
    !disabled;

  const summaryMetricsQuery = useQuery({
    queryKey: [
      `${queryKey}-summary`,
      metricsToSummarize,
      activeAccountWebClientConfig,
    ],
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }
      return tryInvokeApi(
        () =>
          getCatalogSummaryMetrics!(
            new CatalogClient(activeAccountWebClientConfig),
            metricsToSummarize,
            mergeCatalogMetrics
          ),
        (error) => {
          // Since this is just loading metrics, we have the data so we'll just ignore that metrics error
          trackError(
            `${CatalogClient.name}.${CatalogClient.prototype.getSummaryMetricsForListings.name}`,
            error,
            {
              eventIds: Array.from(eventIdToIdsMap.keys()).toSorted(),
              metricsToSummarize: metricsToSummarize,
            }
          );
        }
      );
    },

    enabled: shouldQuery,
    staleTime: Infinity, // Since we're always refetching on an interval, we don't want query to calculate whether the data is stale
    refetchOnWindowFocus: false,
    refetchInterval: DATA_REFRESH_RATE_IN_MILLIS_LONG,
  });

  return {
    summaryMetrics: summaryMetricsQuery.data ?? undefined,
    isLoading: summaryMetricsQuery.isLoading,
    refreshMetrics: () => summaryMetricsQuery.refetch(),
  };
}
